import type { Form } from "@repo/app-types/entities/form";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import { TableSkeleton } from "@repo/ui/Skeletons/skeletons";
import { SmallTimeStamp } from "@repo/ui/SmallTimeStamp/smallTimeStamp";
import { IconPencil, IconPlus, IconTrash } from "@tabler/icons-react";
import { type FC, useState } from "react";
import {
  useAllFormsQuery,
  useDeleteFormMutation,
} from "../../../api/entities/form.js";
import { PageLayout } from "../../../components/layout/pageLayout.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { settingsSections } from "../settingsSections.js";
import { SettingsSection } from "../shared/settingsSection.js";
import { SettingsTable } from "../shared/settingsTable.js";
import { SettingsTableBodySection } from "../shared/settingsTableBodySection.js";
import { SettingsTableDataCell } from "../shared/settingsTableDataCell.js";
import { SettingsTableEmptyBodyContent } from "../shared/settingsTableEmptyBodyContent.js";
import { SettingsTableHeaderCell } from "../shared/settingsTableHeaderCell.js";
import { SettingsTableHeaderSection } from "../shared/settingsTableHeaderSection.js";
import { PreInterviewFormEditorSlideover } from "./preInterviewFormEditorSlideover.js";

export const PreInterviewFormSettingsSection: FC = () => {
  const { data: allForms, isLoading } = useAllFormsQuery();
  const [deleteForm, { isLoading: isDeleting }] = useDeleteFormMutation();

  const [selectedForm, setSelectedForm] = useState<Form | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formIdToDelete, setFormIdToDelete] = useState<string | undefined>(
    undefined,
  );

  return (
    <PageLayout>
      <PageHeader
        width={PageWidth.Form}
        title={settingsSections.forms.label}
        actions={
          <Can
            I={PermissionActions.CREATE}
            a={PermissionSubjects.FORMS}
            passThrough={true}
          >
            {(allowed) => (
              <Button
                size={Size.SMALL}
                variant={ButtonVariant.Primary}
                icon={<IconPlus className="size-4" />}
                onClick={() => setIsCreating(true)}
                isDisabled={!allowed}
              >
                Create form
              </Button>
            )}
          </Can>
        }
      />
      <SettingsSection>
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            Collect information from participants before the start of their
            interview. Email and name are always collected by default.
          </div>
          <SettingsTable>
            <SettingsTableHeaderSection>
              <tr>
                <SettingsTableHeaderCell className="w-[80%]">
                  Name
                </SettingsTableHeaderCell>
                <SettingsTableHeaderCell className="w-[20%]">
                  Created
                </SettingsTableHeaderCell>
              </tr>
            </SettingsTableHeaderSection>
            <SettingsTableBodySection>
              {isLoading ? <TableSkeleton /> : null}

              {allForms?.length === 0 ? (
                <SettingsTableEmptyBodyContent colSpan={3}>
                  No forms have been created yet.
                </SettingsTableEmptyBodyContent>
              ) : null}

              {allForms?.map((form) => (
                <tr key={form.id} className="group relative">
                  <SettingsTableDataCell>
                    <p className="font-semibold">{form.name}</p>
                  </SettingsTableDataCell>
                  <SettingsTableDataCell>
                    <SmallTimeStamp
                      timestamp={form.created_at}
                      className="text-sm text-muted-foreground"
                    />

                    <div className="absolute top-1/2 right-3 hidden -translate-y-1/2 cursor-pointer items-center space-x-2 text-muted-foreground group-hover:flex">
                      <Can
                        I={PermissionActions.UPDATE}
                        a={PermissionSubjects.FORMS}
                      >
                        <IconButton
                          variant={ButtonVariant.Secondary}
                          icon={<IconPencil className="h-4 w-4" />}
                          onClick={() => {
                            setSelectedForm(form);
                          }}
                        />
                      </Can>
                      <Can
                        I={PermissionActions.DELETE}
                        a={PermissionSubjects.FORMS}
                      >
                        <IconButton
                          variant={ButtonVariant.Secondary}
                          icon={<IconTrash className="h-4 w-4" />}
                          onClick={() => {
                            setFormIdToDelete(form.id);
                          }}
                        />
                      </Can>
                    </div>
                  </SettingsTableDataCell>
                </tr>
              ))}
            </SettingsTableBodySection>
          </SettingsTable>
        </div>
      </SettingsSection>

      <SimpleModal
        isOpen={!!formIdToDelete}
        onCancel={() => {
          setFormIdToDelete(undefined);
        }}
        variant={SimpleModalVariant.Warning}
        title="Delete form"
        subtitle={
          "Are you sure you want to delete this form? This action can't be undone."
        }
        confirmButtonText="Delete form"
        onConfirm={() => {
          if (formIdToDelete) {
            deleteForm(formIdToDelete);
          }

          setFormIdToDelete(undefined);
        }}
      />

      <PreInterviewFormEditorSlideover
        form={selectedForm || undefined}
        isOpen={!!selectedForm || isCreating}
        onClose={() => {
          setSelectedForm(null);
          setIsCreating(false);
        }}
      />
    </PageLayout>
  );
};
